import { supabase } from '../lib/supabase';
import type {
  SharedMedia,
  SharedMediaCategory,
  SharedMediaFilters,
  UpdateSharedMediaInput,
} from '../types/sharedMedia';
import { logger } from '../lib/logger';

/**
 * Get all shared media with optional filters
 */
export async function getSharedMedia(filters?: SharedMediaFilters): Promise<SharedMedia[]> {
  let query = supabase
    .from('shared_media')
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .order('created_at', { ascending: false });

  if (filters?.category) {
    query = query.eq('category', filters.category);
  }

  if (filters?.product_id) {
    query = query.eq('product_id', filters.product_id);
  }

  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  if (filters?.search) {
    query = query.or(`filename.ilike.%${filters.search}%,alt_text.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

/**
 * Get shared media by product ID
 */
export async function getSharedMediaByProduct(productId: string): Promise<SharedMedia[]> {
  const { data, error } = await supabase
    .from('shared_media')
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .eq('product_id', productId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Get a single shared media item by ID
 */
export async function getSharedMediaById(id: string): Promise<SharedMedia | null> {
  const { data, error } = await supabase
    .from('shared_media')
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Upload shared media (admin only)
 */
export async function uploadSharedMedia(
  userId: string,
  file: File,
  category: SharedMediaCategory,
  productId?: string,
  altText?: string
): Promise<SharedMedia> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
  const storagePath = `shared/${category}/${fileName}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from('media')
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data: urlData } = supabase.storage.from('media').getPublicUrl(storagePath);

  // Get image dimensions if applicable
  let width: number | undefined;
  let height: number | undefined;

  if (file.type.startsWith('image/')) {
    try {
      const dimensions = await getImageDimensions(file);
      width = dimensions.width;
      height = dimensions.height;
    } catch {
      // Ignore dimension errors
    }
  }

  // Insert into database
  const { data, error } = await supabase
    .from('shared_media')
    .insert({
      filename: file.name,
      storage_path: storagePath,
      url: urlData.publicUrl,
      mime_type: file.type,
      size: file.size,
      width,
      height,
      alt_text: altText,
      category,
      product_id: productId || null,
      created_by: userId,
    })
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .single();

  if (error) {
    // Cleanup storage on error
    await supabase.storage.from('media').remove([storagePath]);
    throw error;
  }

  return data;
}

/**
 * Update shared media (admin only)
 */
export async function updateSharedMedia(
  id: string,
  input: UpdateSharedMediaInput
): Promise<SharedMedia> {
  const updateData: Record<string, unknown> = {};

  if (input.category !== undefined) {
    updateData.category = input.category;
  }

  if (input.product_id !== undefined) {
    updateData.product_id = input.product_id;
  }

  if (input.alt_text !== undefined) {
    updateData.alt_text = input.alt_text;
  }

  if (input.is_active !== undefined) {
    updateData.is_active = input.is_active;
  }

  const { data, error } = await supabase
    .from('shared_media')
    .update(updateData)
    .eq('id', id)
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Toggle active status (admin only)
 */
export async function toggleSharedMediaActive(id: string): Promise<SharedMedia> {
  // Get current status
  const { data: current, error: fetchError } = await supabase
    .from('shared_media')
    .select('is_active')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Toggle status
  const { data, error } = await supabase
    .from('shared_media')
    .update({ is_active: !current.is_active })
    .eq('id', id)
    .select(`
      *,
      product:products(id, name, slug)
    `)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete shared media (admin only)
 */
export async function deleteSharedMedia(id: string): Promise<void> {
  // Get storage path first
  const { data: media, error: fetchError } = await supabase
    .from('shared_media')
    .select('storage_path')
    .eq('id', id)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!media) throw new Error('Media not found');

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('media')
    .remove([media.storage_path]);

  if (storageError) {
    logger.error('Storage delete error:', storageError);
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('shared_media')
    .delete()
    .eq('id', id);

  if (dbError) throw dbError;
}

/**
 * Delete multiple shared media items (admin only)
 */
export async function deleteMultipleSharedMedia(ids: string[]): Promise<void> {
  // Get storage paths
  const { data: mediaList, error: fetchError } = await supabase
    .from('shared_media')
    .select('id, storage_path')
    .in('id', ids);

  if (fetchError) throw fetchError;
  if (!mediaList || mediaList.length === 0) return;

  const storagePaths = mediaList.map((m) => m.storage_path);

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from('media')
    .remove(storagePaths);

  if (storageError) {
    logger.error('Storage delete error:', storageError);
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('shared_media')
    .delete()
    .in('id', ids);

  if (dbError) throw dbError;
}

/**
 * Get all products for dropdown selection
 */
export async function getProductsForSelection(): Promise<{ id: string; name: string; slug: string }[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, slug')
    .order('name');

  if (error) throw error;
  return data || [];
}

/**
 * Helper to get image dimensions
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
